import styled, { css } from "styled-components";
import { primaryButtonStyles } from "./theme/primaryButtonStyles";
import { secondaryButtonStyles } from "./theme/secondaryButtonStyles";
import { normalButtonStyles } from "./theme/normalButtonStyles";
import { strongButtonStyles } from "./theme/strongButtonStyles";
import { errorButtonStyles } from "./theme/errorButtonStyles";

interface StyledButtonProps {
  $color: "primary" | "secondary" | "normal" | "strong";
  $type: "solid" | "outline" | "text";
  $size: "large" | "medium" | "small";
  $width: string;
  $height: string | undefined;
  $error?: boolean;
  $maxWidth?: string;
  $dark: boolean;
  $readonly?: boolean;
}

export const StyledButton = styled.button<StyledButtonProps>`
  /* transition: all 0.1s ease; */
  box-sizing: border-box;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  border-radius: 0.4rem;
  flex-shrink: 0;
  cursor: pointer;
  width: ${({ $width }) => $width};
  max-width: ${({ $maxWidth }) => $maxWidth};
  pointer-events: ${({ $readonly }) => ($readonly ? "none" : "initial")};
  ${({ $size }) => {
    if ($size === "large")
      return css`
        height: 4rem;
      `;
    if ($size === "medium")
      return css`
        height: 2.8rem;
      `;
    if ($size === "small")
      return css`
        height: 2rem;
      `;
  }}
  ${({ $height }) => {
    if ($height)
      return css`
        height: ${$height};
      `;
  }}
  &>p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > .icon {
    flex-shrink: 0;
  }
  & > svg path {
    stroke-width: 1.8;
  }
  &:disabled {
    pointer-events: none;
  }
  ${({ $size }) => {
    if ($size === "large")
      return css`
        padding: 0.8rem;
        & > p {
          font: var(--button-large);
        }
        & > svg {
          width: 2rem;
          height: 2rem;
        }
      `;
    if ($size === "medium")
      return css`
        padding: 0.6rem 0.8rem;
        & > p {
          font: var(--button-small);
        }
        & > svg {
          width: 1.6rem;
          height: 1.6rem;
        }
      `;
    if ($size === "small")
      return css`
        padding: 0.4rem;
        & > p {
          font: var(--label-small-600);
        }
        & > svg {
          width: 1.2rem;
          height: 1.2rem;
        }
      `;
  }}

  ${({ $color, $type, $dark, $error }) => {
    if ($error) return errorButtonStyles($dark);
    if ($color === "primary") return primaryButtonStyles($type, $dark);
    if ($color === "secondary") return secondaryButtonStyles($type, $dark);
    if ($color === "normal") return normalButtonStyles($type, $dark);
    if ($color === "strong") return strongButtonStyles($type, $dark);
  }}
`;

interface StyledButtonGroupProps {
  $width?: string;
}

export const StyledButtonGroup = styled.section<StyledButtonGroupProps>`
  box-sizing: border-box;
  border-radius: 0.4rem;
  background-color: var(--bg-gray-0);
  box-shadow: inset 0 0 0 0.1rem var(--bg-gray-1);
  width: ${({ $width }) => $width || "100%"};
  > p {
    font: var(--label-small-400);
    color: var(--text-gray-strong);
    margin-bottom: 0.4rem;
  }
  > div {
    display: flex;
    justify-content: space-between;
  }
`;
